/**
 * CustomerDetailsModalPremium - Modal de detalhes premium do cliente
 * Layout moderno com sidebar à DIREITA e abas: Resumo, Negócios, Chat, Atividades
 * Centro de visão 360º do cliente para vendedores
 * 
 * CHANGELOG:
 *   RELEASE: 2026-09-07 v1.13.0 - Modo contato passa a completar a ficha com os
 *            campos contact_* da RPC get_contact_linked_customer v2.0.0 quando
 *            o contactPreview não é informado (corrige ficha vazia aberta pelo
 *            card do funil de negócios).
 *   RELEASE: 2026-08-20 v1.12.0 - Responsividade mobile (padrão DealDetailsModalPremium):
 *            modal em tela cheia no celular e sidebar injetada no topo da aba Resumo.
 *   RELEASE: 2026-08-19 v1.11.0 - Conversão "Contato → Cliente" direto no modal
 *            (header + ações rápidas), reaproveitando CustomerFormDialog com
 *            prefillContact. Após criar, o modal passa a operar em modo Cliente.
 * @version 1.11.0
 * RELEASE: 2026-01-10
 */
import { useTenantStockRealtime } from '@/hooks/realtime/useTenantStockRealtime';
import { useServiceOrderModuleEnabled } from '@/hooks/useServiceOrderModule';
import { useServiceOrdersList } from '@/hooks/useServiceOrders';
import { ServiceOrdersEmbeddedList } from '@/components/service-orders/ServiceOrdersEmbeddedList';
import React, { useState, useCallback } from 'react';
// v1.12.0: detecção de viewport mobile para layout em tela cheia
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { EditContactDialog } from '@/components/tenant/contacts/EditContactDialog';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { useCrmCustomerById, CrmCustomer } from '@/hooks/useCrmCustomers';
import { useCustomerModalData } from '@/hooks/useCustomerModalData';
import { CustomerFormInline } from './CustomerFormInline';
// v1.11.0: formulário reutilizado para transformar contato em cliente
import { CustomerFormDialog } from '@/components/crm/customers/CustomerFormDialog';
import { CustomerModalHeader } from './CustomerModalHeader';
import { CustomerModalTabs, CustomerModalTab } from './CustomerModalTabs';
import { CustomerModalSidebar } from './CustomerModalSidebar';
import { CustomerSummaryTab } from './tabs/CustomerSummaryTab';
import { CustomerDealsTab } from './tabs/CustomerDealsTab';
import { CustomerChatTab } from './tabs/CustomerChatTab';
import { CustomerActivityTimelinePremium } from './components/CustomerActivityTimelinePremium';
import { CustomerInlineActivityCreator } from './components/CustomerInlineActivityCreator';

import { CustomerContractsTab } from './tabs/CustomerContractsTab';
import { CustomerDocumentsTab } from './tabs/CustomerDocumentsTab';
// v1.9.0: histórico de campanhas disparadas (carregado apenas ao abrir a aba)
import { CustomerCampaignsTab } from './tabs/CustomerCampaignsTab';
// v1.10.0: aba Propostas (todas as propostas dos negócios do cliente/contato)
import { CustomerProposalsTab } from './tabs/CustomerProposalsTab';

import { CustomerFinancialTab } from '@/components/financial/CustomerFinancialTab';
// v: subabas por tipo de tarefa (Normal | Retenção | Jurídico)
import { TasksRelatedTabs } from '@/components/tasks/related/TasksRelatedTabs';
// Módulo de Veículos (flag por tenant + listagem reutilizável)
import { VehiclesList } from '@/components/vehicles/VehiclesList';
import { useVehiclesModule } from '@/hooks/useVehiclesModule';
import { useFinanceiroCobrancasPaginated } from '@/hooks/useFinanceiroCobrancasPaginated';
import { TimelineContainer } from '@/components/crm/TimelineContainer';
// v1.6.0: import lazy para quebrar dependência circular
// (DealDetailsModalPremium importa este mesmo módulo, o que deixava bindings
// indefinidos em tempo de avaliação — ex.: CustomerInlineActivityCreator)
const DealDetailsModalPremium = React.lazy(() =>
  import('@/components/crm/deals/modal/DealDetailsModalPremium').then((m) => ({
    default: m.DealDetailsModalPremium,
  }))
);
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

/** Preview mínimo do contato (evita RPC extra ao abrir pela listagem) */
export interface ContactPreview {
  id: string;
  name: string | null;
  phone?: string | null;
  email?: string | null;
  avatar_url?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  /** v1.5.0: vendedor vinculado ao contato */
  seller_id?: string | null;
  seller_name?: string | null;
  seller_avatar?: string | null;
}

/** Props do modal premium */
interface CustomerDetailsModalPremiumProps {
  customerId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (customer: CrmCustomer) => void;
  /**
   * v1.4.0 — Modo Contato.
   * Quando informado, o modal resolve se o contato possui cliente vinculado:
   *  - com cliente  → abre o modal completo do cliente (comportamento padrão)
   *  - sem cliente  → abre em "modo contato" (abas Negócios, Chat e Tarefas)
   */
  contactId?: string | null;
  /** Dados já conhecidos do contato, usados na renderização do modo contato */
  contactPreview?: ContactPreview | null;
}

/** Modal premium de detalhes do cliente */
export function CustomerDetailsModalPremium({ 
  customerId, 
  open, 
  onOpenChange,
  onEdit,
  contactId = null,
  contactPreview = null
}: CustomerDetailsModalPremiumProps) {
  const { currentTenant } = useAuth();
  // v1.12.0: no mobile o modal ocupa a tela toda e a sidebar vai para o Resumo
  const isMobile = useIsMobile();
  // v1.3.0: Permissões granulares — Financeiro (financial_charges) e Contratos (crm_contracts)
  const { canView, canEdit, canDelete, canCreate } = usePermissions();
  // v1.11.0: permissão para criar clientes (habilita a conversão contato → cliente)
  const canCreateCustomers = canCreate('crm_customers');
  const canViewCharges = canView('financial_charges');
  const canEditCharges = canEdit('financial_charges');
  const canDeleteCharges = canDelete('financial_charges');
  const canViewContracts = canView('crm_contracts');
  // v1.10.0: permissões do módulo de Propostas
  const canViewProposals = canView('crm_proposals');
  // 2026-10-09: aba O.S (módulo OS + permissão de listagem); total via KPI do servidor
  const serviceOrdersEnabled = useServiceOrderModuleEnabled() && canView('os_list');
  const { data: osCountData } = useServiceOrdersList({
    search: '', status: null, serviceTypeId: null, providerId: null, categoryId: null, phaseId: null,
    deadlineBucket: null, dateField: 'created_at', dateFrom: null, dateTo: null,
    sortField: 'created_at', sortDirection: 'desc', page: 1, pageSize: 1, customerId,
  }, open && !!customerId && serviceOrdersEnabled);
  // 2026-10-11: aba O.S./equipamentos atualizam pelo canal único de estoque do tenant
  useTenantStockRealtime([['service-orders'], ['vehicle-equipment']], open && !!customerId);
  const canEditProposals = canEdit('crm_proposals');
  const canEditContracts = canEdit('crm_contracts');
  const canDeleteContracts = canDelete('crm_contracts');
  const queryClient = useQueryClient();
  // v1.4.0: no modo contato a aba inicial é "Negócios" (Resumo não existe)
  const [activeTab, setActiveTab] = useState<CustomerModalTab>('summary');
  const [isEditMode, setIsEditMode] = useState(false);
  
  
  // Estados para modais sobrepostos
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);

  // v1.3.0: Se a aba ativa virar 'charges'/'contracts' sem permissão, retorna ao 'summary'
  React.useEffect(() => {
    if (activeTab === 'charges' && !canViewCharges) {
      setActiveTab('summary');
    }
    if (activeTab === 'contracts' && !canViewContracts) {
      setActiveTab('summary');
    }
  }, [activeTab, canViewCharges, canViewContracts]);

  // v1.4.0: dialog de edição do contato (modo contato)
  const [editContactOpen, setEditContactOpen] = useState(false);
  // v1.11.0: dialog de conversão contato → cliente + id do cliente resultante
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertedCustomerId, setConvertedCustomerId] = useState<string | null>(null);

  // Busca dados do cliente
  // v1.4.0: em modo contato, resolve primeiro o cliente vinculado (se houver)
  const { data: linkedCustomer, isLoading: isLoadingLink } = useQuery({
    // Mesma queryKey usada em ContactSidebar.tsx — cache compartilhado
    queryKey: ['contact-linked-customer', contactId, currentTenant?.id],
    queryFn: async () => {
      if (!contactId) return null;
      const { data, error } = await supabase.rpc('get_contact_linked_customer', {
        p_contact_id: contactId,
      });
      if (error) throw error;
      // v1.13.0: a RPC v2.0.0 retorna SEMPRE uma linha — quando não há cliente
      // vinculado, id/nome vêm nulos e os campos contact_* trazem os dados do
      // contato (fonte confiável para preencher a ficha mesmo sem preview).
      return data && data.length > 0 ? data[0] : null;
    },
    enabled: open && !!contactId && !customerId && !!currentTenant?.id,
  });

  // ID efetivo do cliente: explícito > recém-convertido > vinculado ao contato
  // v1.11.0: `convertedCustomerId` permite trocar para o modo Cliente sem
  // fechar/reabrir o modal logo após a conversão.
  const effectiveCustomerId = customerId ?? convertedCustomerId ?? linkedCustomer?.id ?? null;

  const { data: realCustomer, isLoading: isLoadingCustomer, error } = useCrmCustomerById(effectiveCustomerId);

  // v1.11.0: limpa o cliente convertido quando o modal fecha ou muda de contato
  React.useEffect(() => {
    if (!open) {
      setConvertedCustomerId(null);
      setConvertOpen(false);
    }
  }, [open]);
  React.useEffect(() => {
    setConvertedCustomerId(null);
  }, [contactId]);

  // Modo contato: há contactId, o link já foi resolvido e não existe cliente
  const isContactMode = !!contactId && !effectiveCustomerId && !isLoadingLink;

  /**
   * "Cliente virtual" — mapeia os dados do contato para o formato CrmCustomer,
   * permitindo reaproveitar 100% do header/sidebar/abas sem duplicar componentes.
   */
  const virtualCustomer: CrmCustomer | null = React.useMemo(() => {
    if (!isContactMode || !contactId) return null;
    // v1.13.0: preview (card/listagem) tem prioridade; os campos contact_* da
    // RPC get_contact_linked_customer completam o que não veio no preview.
    const contactName =
      contactPreview?.name ?? linkedCustomer?.contact_name ?? 'Contato';
    const contactPhone =
      contactPreview?.phone ?? linkedCustomer?.contact_phone ?? null;
    const contactEmail =
      contactPreview?.email ?? linkedCustomer?.contact_email ?? null;
    const contactAvatar =
      contactPreview?.avatar_url ?? linkedCustomer?.contact_avatar_url ?? null;
    const sellerId =
      contactPreview?.seller_id ?? linkedCustomer?.contact_seller_id ?? null;
    const sellerName =
      contactPreview?.seller_name ?? linkedCustomer?.contact_seller_name ?? 'Vendedor';
    const sellerAvatar =
      contactPreview?.seller_avatar ?? linkedCustomer?.contact_seller_avatar ?? null;
    return {
      id: contactId,
      customer_type: 'pf',
      // `nome` é o campo lido por getDisplayName no header/sidebar (cliente PF)
      nome: contactName,
      name: contactName,
      main_contact_id: contactId,
      main_contact: {
        id: contactId,
        name: contactName,
        phone: contactPhone,
        email: contactEmail,
        avatar_url: contactAvatar,
      },
      // Vendedor vinculado ao contato (exibido na sidebar em modo somente leitura)
      seller: sellerId
        ? {
            id: sellerId,
            name: sellerName,
            avatar_url: sellerAvatar,
            email: null,
          }
        : null,
      lifecycle_stage: 'active',
      created_at:
        contactPreview?.created_at ??
        linkedCustomer?.contact_created_at ??
        new Date().toISOString(),
      updated_at:
        contactPreview?.updated_at ??
        linkedCustomer?.contact_updated_at ??
        new Date().toISOString(),
    } as unknown as CrmCustomer;
  }, [isContactMode, contactId, contactPreview, linkedCustomer]);

  const customer = isContactMode ? virtualCustomer : realCustomer;
  const isLoading = isLoadingLink || (!isContactMode && isLoadingCustomer);


  /**
   * Handler para quando o contato principal é atualizado
   * Invalida o cache do cliente para recarregar dados sem fechar o modal
   */
  const handleContactUpdated = useCallback(() => {
    if (effectiveCustomerId) {
      // Invalida cache do cliente específico
      queryClient.invalidateQueries({ queryKey: ['crm-customer', effectiveCustomerId] });
      // Invalida lista de clientes (caso apareça em outras telas)
      queryClient.invalidateQueries({ queryKey: ['crm-customers'] });
      // Invalida dados do modal (contadores)
      queryClient.invalidateQueries({ queryKey: ['customer-modal-data', effectiveCustomerId] });
    }
    if (contactId) {
      // Modo contato: atualiza listagem e vínculo
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contact-linked-customer', contactId] });
    }
  }, [effectiveCustomerId, contactId, queryClient]);
  
  /**
   * v1.11.0: Conclusão da conversão contato → cliente.
   * Guarda o id do cliente criado (ou da duplicata escolhida) e invalida
   * os caches que dependem do vínculo contato ↔ cliente.
   */
  const handleConverted = useCallback((newCustomerId: string) => {
    setConvertedCustomerId(newCustomerId);
    setConvertOpen(false);
    setActiveTab('summary');
    queryClient.invalidateQueries({ queryKey: ['contact-linked-customer', contactId] });
    queryClient.invalidateQueries({ queryKey: ['crm-customers'] });
    queryClient.invalidateQueries({ queryKey: ['crm-customers-paginated'] });
    queryClient.invalidateQueries({ queryKey: ['contacts'] });
  }, [contactId, queryClient]);

  // Query unificada para contadores das abas
  // v1.6.0: no modo Contato os contadores vêm pelo contato (mesma RPC, param p_contact_id)
  const { data: modalData } = useCustomerModalData(
    effectiveCustomerId,
    { enabled: open },
    isContactMode ? contactId : null
  );




  // Contadores para abas
  const dealsCount = modalData?.counters.total_deals || 0;
  const chatCount = modalData?.counters.conversations || 0;
  const contractsCount = modalData?.counters.contracts || 0;
  const documentsCount = modalData?.counters.documents || 0;
  // v1.1.0: contador de tarefas vinculadas
  const tasksCount = modalData?.counters.tasks || 0;
  // v1.8.0: contador de veículos ativos (badge da aba Veículos)
  const vehiclesCount = modalData?.counters.vehicles || 0;
  // v1.10.0: contador de propostas (badge da aba Propostas)
  const proposalsCount = modalData?.counters.proposals || 0;

  // v1.7.0: módulo de Veículos (flag do tenant) + contagem para o badge da aba
  const { isEnabled: vehiclesEnabled } = useVehiclesModule();


  // v1.2.0: Contador de cobranças financeiras via RPC paginada (evita SELECT direto na tabela)
  const { totalCount: chargesCount } = useFinanceiroCobrancasPaginated(
    effectiveCustomerId ? { customer_id: effectiveCustomerId } : undefined,
    { page: 1, pageSize: 1 }
  );

  // Handlers
  const handleOpenConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setIsChatModalOpen(true);
  };

  const handleOpenDeal = (dealId: string) => {
    setSelectedDealId(dealId);
    setIsDealModalOpen(true);
  };

  /* Ações de status/exclusão removidas: cliente é sempre Ativo */




  // v1.4.0: no modo contato apenas Negócios/Chat/Tarefas/Veículos/Campanhas existem — força aba válida
  React.useEffect(() => {
    // v1.6.0: 'summary' agora é suportado no modo Contato (timeline por contact_id)
    if (isContactMode && !['summary', 'deals', 'chat', 'tasks', 'vehicles', 'campaigns', 'proposals'].includes(activeTab)) {
      setActiveTab('summary');
    }
  }, [isContactMode, activeTab]);


  /**
   * v1.5.0: aba EFETIVA calculada durante o render.
   * O useEffect acima só roda após o commit — no modo Contato isso permitia
   * um render com a aba "summary" (timeline de atividades) usando o id do
   * contato como se fosse cliente, disparando erro 22023 na RPC
   * get_customer_activities_paginated. Calcular aqui elimina esse render.
   */
  const effectiveTab: CustomerModalTab =
    isContactMode && !['summary', 'deals', 'chat', 'tasks', 'vehicles', 'campaigns', 'proposals'].includes(activeTab)
      ? 'summary'
      : activeTab;


  /**
   * Handler para ativar modo de edição inline
   * Modo contato → abre o dialog de edição de contato
   * Se onEdit externo for fornecido, usa ele (fecha modal e abre externo)
   * Caso contrário, ativa edição inline dentro do modal
   */
  const handleEdit = () => {
    if (isContactMode) {
      setEditContactOpen(true);
    } else if (customer && onEdit) {
      onOpenChange(false);
      onEdit(customer);
    } else {
      // Ativa modo de edição inline dentro do modal
      setIsEditMode(true);
    }
  };


  /**
   * Handler para quando a edição inline é concluída com sucesso
   * Desativa modo de edição e recarrega dados do cliente
   */
  const handleEditSuccess = () => {
    setIsEditMode(false);
    handleContactUpdated();
  };

  /**
   * v1.12.0: ficha lateral do cliente/contato.
   * No desktop é renderizada à direita; no mobile é injetada no topo do Resumo.
   */
  const renderSidebar = () =>
    customer ? (
      <CustomerModalSidebar
        customer={customer}

        onContactUpdated={handleContactUpdated}
        isContactMode={isContactMode}
        onConvertToCustomer={canCreateCustomers ? () => setConvertOpen(true) : undefined}
      />
    ) : null;

  /** Renderiza conteúdo da aba ativa */
  const renderTabContent = () => {
    if (!customer) return null;


    switch (effectiveTab) {
      case 'summary':
        // v1.6.0: no modo Contato o Resumo usa a timeline por contact_id
        // (mesma estrutura de andamentos do cliente — nada é duplicado quando
        // o contato virar cliente)
        return (
          <CustomerSummaryTab
            customer={customer}
            modalData={modalData || null}
            isActive={effectiveTab === 'summary'}
            isContactMode={isContactMode}
            contactId={isContactMode ? contactId : null}
            sidebarContent={isMobile ? renderSidebar() : undefined}
          />
        );
      case 'deals':
        return (
          <CustomerDealsTab 
            customerId={isContactMode ? null : customer.id}
            contactId={customer.main_contact_id}
            isActive={effectiveTab === 'deals'}
            onOpenDeal={handleOpenDeal}
          />
        );
      case 'chat':
        return (
          <CustomerChatTab 
            contactId={customer.main_contact_id}
            contactName={customer.main_contact?.name ?? null}
            isActive={effectiveTab === 'chat'}
            onOpenConversation={handleOpenConversation}
          />
        );
      case 'activities':
        // v1.3.0: removido TimelineContainer (ScrollArea interno) — usa scroll externo do wrapper,
        // igual à aba Resumo, evitando barra de rolagem aninhada e espaço em branco lateral
        // v1.4.0: criador inline de andamentos no topo (mesmo padrão do modal de negócios)
        return (
          <div className="space-y-3">
            <CustomerInlineActivityCreator
              customerId={isContactMode ? null : customer.id}
              contactId={isContactMode ? contactId : null}
            />
            <CustomerActivityTimelinePremium 
              customerId={isContactMode ? null : customer.id}
              contactId={isContactMode ? contactId : null}
              isActive={effectiveTab === 'activities'}
            />
          </div>
        );

      case 'contracts':
        // v1.3.0: defesa em profundidade — oculta conteúdo se sem permissão
        if (!canViewContracts) return null;
        return (
          <CustomerContractsTab
            customerId={customer.id}
            contactId={customer.main_contact_id}
            isActive={effectiveTab === 'contracts'}
            canEdit={canEditContracts}
            canDelete={canDeleteContracts}
          />
        );
      case 'documents':
        return (
          <CustomerDocumentsTab
            customerId={customer.id}
            isActive={effectiveTab === 'documents'}
          />
        );
      case 'charges':
        // v1.3.0: defesa em profundidade — oculta conteúdo se sem permissão
        if (!canViewCharges) return null;
        return (
          <CustomerFinancialTab
            customerId={customer.id}
            isActive={effectiveTab === 'charges'}
            canEdit={canEditCharges}
            canDelete={canDeleteCharges}
          />

        );
      case 'tasks':
        // v1.1.0: lista de tarefas vinculadas ao contato principal do cliente
        // Defesa em profundidade: oculta conteúdo se módulo de Tarefas estiver desabilitado no tenant
        if (!(currentTenant?.tasks_enabled ?? false)) return null;
        return <TasksRelatedTabs context={{ contactId: customer.main_contact_id }} />;
      case 'vehicles':
        // v1.7.0: veículos do cliente (ou do contato, quando não há cliente vinculado)
        // Defesa em profundidade: só renderiza com o módulo habilitado no tenant
        if (!vehiclesEnabled) return null;
        return (
          <VehiclesList
            isActive={effectiveTab === 'vehicles'}
            scope={
              isContactMode
                ? { contactId: customer.main_contact_id }
                : // Mesmo critério do contador (get_customer_modal_data):
                  // veículos do cliente OU do contato principal
                  { customerId: customer.id, contactId: customer.main_contact_id }
            }
          />
        );
      case 'proposals':
        // v1.10.0: propostas de todos os negócios do cliente (ou do contato)
        // Defesa em profundidade: exige módulo CRM e permissão de visualização
        if (!(currentTenant?.crm_enabled ?? true) || !canViewProposals) return null;
        return (
          <CustomerProposalsTab
            isActive={effectiveTab === 'proposals'}
            customerId={isContactMode ? null : customer.id}
            contactId={isContactMode ? customer.main_contact_id : null}
            canEdit={canEditProposals}
          />
        );
      case 'service_orders':
        // 2026-10-09: mesma listagem da aba O.S do Veículo, filtrada pelo cliente
        if (isContactMode || !serviceOrdersEnabled) return null;
        return <ServiceOrdersEmbeddedList customerId={customer.id} />;
      case 'campaigns':
        // v1.9.0: histórico de disparos de campanha — a RPC só é chamada com a aba ativa
        return (
          <CustomerCampaignsTab
            isActive={effectiveTab === 'campaigns'}
            customerId={isContactMode ? null : customer.id}
            contactId={isContactMode ? customer.main_contact_id : null}
          />
        );
      default:
        return null;
    }
  };

  /** Renderiza conteúdo interno do modal */
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-3 px-4 h-12 border-b">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-48 flex-1" />
          </div>
          <div className="flex items-center gap-2 h-9 px-4 border-b">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-5 w-20" />)}
          </div>
          <div className="flex flex-1 min-h-0">
            <div className="w-[320px] border-r p-3 space-y-4">
              <Skeleton className="h-16 w-16 rounded-full mx-auto" />
              <Skeleton className="h-4 w-32 mx-auto" />
              <Skeleton className="h-32" />
            </div>
            <div className="flex-1 p-4 space-y-4">
              <div className="grid grid-cols-4 gap-3">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 rounded-lg" />)}
              </div>
              <Skeleton className="h-48 rounded-lg" />
            </div>
          </div>
        </div>
      );
    }

    if (error || !customer) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <p className="text-muted-foreground">Cliente não encontrado.</p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </div>
      );
    }

    // MODO EDIÇÃO INLINE - substitui todo o conteúdo pelo formulário
    if (isEditMode) {
      return (
        <CustomerFormInline
          customer={customer}
          onCancel={() => setIsEditMode(false)}
          onSuccess={handleEditSuccess}
        />
      );
    }

    // MODO VISUALIZAÇÃO - conteúdo normal do modal
    return (
      <TooltipProvider>
        <div className="flex flex-col h-full">
          <CustomerModalHeader
            customer={customer}
            onEdit={handleEdit}
            onClose={() => onOpenChange(false)}

            isContactMode={isContactMode}
            onConvertToCustomer={canCreateCustomers ? () => setConvertOpen(true) : undefined}
          />

          <CustomerModalTabs
            activeTab={effectiveTab}
            onTabChange={setActiveTab}
            dealsCount={dealsCount}
            chatCount={chatCount}
            contractsCount={contractsCount}
            documentsCount={documentsCount}
            chargesCount={chargesCount}
            tasksCount={tasksCount}
            vehiclesCount={vehiclesCount}
            proposalsCount={proposalsCount}
            serviceOrdersCount={osCountData?.kpis?.total}
            serviceOrdersEnabled={serviceOrdersEnabled && !isContactMode}

            financialEnabled={currentTenant?.financial_enabled ?? false}
            tasksEnabled={currentTenant?.tasks_enabled ?? false}
            vehiclesEnabled={vehiclesEnabled}
            canViewCharges={canViewCharges}
            canViewContracts={canViewContracts}
            crmEnabled={currentTenant?.crm_enabled ?? true}
            canViewProposals={canViewProposals}
            isContactMode={isContactMode}
          />


          {/* Área de conteúdo: Conteúdo + SIDEBAR À DIREITA (padrão) */}
          {/* 
            v1.2.0: ScrollArea REMOVIDO do nível global
            Cada aba agora controla seu próprio scroll (padrão "auto-contido")
            Isso resolve conflito de scroll containers com a tabela de deals
            - overflow-hidden garante clipping visual no limite correto
            - min-w-0 permite que filhos encolham e gerem overflow interno
          */}
          <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
            {/* 
              v1.2.0: Container simples sem ScrollArea global
              Cada aba define seu próprio overflow (vertical ou horizontal)
              - flex-1: ocupa espaço restante
              - min-w-0: permite encolher além do conteúdo natural
              - min-h-0: permite altura flexível
              - overflow-hidden: clip visual no limite desta coluna
            */}
            <div className="flex-1 min-w-0 min-h-0 overflow-y-auto scrollbar-thin p-2 md:p-4">
              {renderTabContent()}
            </div>

            {/* v1.12.0: no mobile a ficha aparece dentro da aba Resumo */}
            {!isMobile && renderSidebar()}


          </div>

          {/* Diálogo "Marcar como Perdido" removido: cliente é sempre Ativo */}


          <ChatConversationModal
            conversationId={selectedConversationId}
            open={isChatModalOpen}
            onOpenChange={(open) => { setIsChatModalOpen(open); if (!open) setSelectedConversationId(null); }}
          />

          {/* v1.6.0: carregado sob demanda (evita ciclo de import) */}
          {isDealModalOpen && (
            <React.Suspense fallback={null}>
              <DealDetailsModalPremium
                dealId={selectedDealId}
                open={isDealModalOpen}
                onOpenChange={(open) => { setIsDealModalOpen(open); if (!open) setSelectedDealId(null); }}
              />
            </React.Suspense>
          )}

          {/* v1.4.0: edição do contato (modo Contato) */}
          {isContactMode && contactId && (
            <EditContactDialog
              open={editContactOpen}
              onOpenChange={setEditContactOpen}
              contact={{
                id: contactId,
                // v1.13.0: fallback para os campos contact_* da RPC quando o
                // preview não foi informado por quem abriu o modal
                name: contactPreview?.name ?? linkedCustomer?.contact_name ?? null,
                phone: contactPreview?.phone ?? linkedCustomer?.contact_phone ?? null,
                email: contactPreview?.email ?? linkedCustomer?.contact_email ?? null,
                avatar_url: contactPreview?.avatar_url ?? linkedCustomer?.contact_avatar_url ?? null,
              }}
              onSuccess={handleContactUpdated}
            />
          )}

          {/* v1.11.0: conversão contato → cliente (mesmo formulário da tela de Clientes) */}
          {isContactMode && contactId && convertOpen && (
            <CustomerFormDialog
              customer={null}
              open={convertOpen}
              onOpenChange={setConvertOpen}
              prefillContact={{
                id: contactId,
                // v1.13.0: fallback para os campos contact_* da RPC quando o
                // preview não foi informado por quem abriu o modal
                name: contactPreview?.name ?? linkedCustomer?.contact_name ?? null,
                phone: contactPreview?.phone ?? linkedCustomer?.contact_phone ?? null,
                email: contactPreview?.email ?? linkedCustomer?.contact_email ?? null,
                // Herda o vendedor responsável do contato para o novo cliente
                seller_id: contactPreview?.seller_id ?? linkedCustomer?.contact_seller_id ?? null,
              }}
              onSuccess={(newCustomer) => {
                if (newCustomer?.id) handleConverted(newCustomer.id);
              }}
              onSelectExistingCustomer={(existing) => {
                if (existing?.id) handleConverted(existing.id);
              }}
            />
          )}
        </div>
      </TooltipProvider>

    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* v1.12.0: tela cheia no mobile (mesmo padrão do modal de negócios) */}
      <DialogContent
        className={cn(
          'p-0 flex flex-col overflow-hidden',
          isMobile
            ? 'w-screen h-[100dvh] max-w-none rounded-none border-0'
            : 'max-w-[95vw] w-[95vw] h-[97vh]',
        )}
        preventCloseOnOutsideInteraction
        hideCloseButton
      >
        <VisuallyHidden.Root><DialogTitle>{isContactMode ? 'Detalhes do Contato' : 'Detalhes do Cliente'}</DialogTitle></VisuallyHidden.Root>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
